import { useEffect, useState, useCallback } from "react";

/**
 * Sidebar has three modes:
 *   "full"    → 240px wide, icons + labels
 *   "icon"    → 60px wide, icons only (tooltips on hover)
 *   "hidden"  → completely off-screen, only a floating hamburger visible
 *
 * On mobile (<768px), the sidebar operates as an overlay drawer regardless
 * of the persisted desktop mode: "hidden" is default; opening it shows a
 * full-width panel over the content until dismissed.
 *
 * State is persisted in localStorage so the choice survives reloads.
 */
const KEY = "myedge_sidebar_mode";
const MODES = ["full", "icon", "hidden"];
const MOBILE_BREAKPOINT = 768;

export function useSidebarMode() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT,
  );
  const [mode, setMode] = useState(() => {
    try {
      const v = localStorage.getItem(KEY);
      return MODES.includes(v) ? v : "full";
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[sidebarMode] localStorage read blocked:", err?.message);
      }
      return "full";
    }
  });

  // Track viewport for mobile behavior.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // On first mobile switch, default to hidden so content is full-width.
  useEffect(() => {
    if (isMobile && mode !== "hidden") {
      setMode("hidden");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, mode);
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.warn("[sidebarMode] localStorage write blocked:", err?.message);
      }
    }
  }, [mode]);

  const cycle = useCallback(() => {
    // On mobile: only toggle open/closed (hidden ↔ full drawer).
    if (isMobile) {
      setMode((prev) => (prev === "hidden" ? "full" : "hidden"));
      return;
    }
    setMode((prev) => MODES[(MODES.indexOf(prev) + 1) % MODES.length]);
  }, [isMobile]);

  return { mode, setMode, cycle, isMobile };
}

export function sidebarWidthClass(mode, isMobile = false) {
  // On mobile the sidebar overlays content — main should stay full width.
  if (isMobile) return "ml-0";
  if (mode === "hidden") return "ml-0";
  if (mode === "icon") return "ml-16";
  return "ml-60";
}

export function statusBarLeftClass(mode, isMobile = false) {
  if (isMobile) return "left-0";
  if (mode === "hidden") return "left-0";
  if (mode === "icon") return "left-16";
  return "left-60";
}
